import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import {
  FaDownload,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import SalesOrderFormModal from './SalesOrderFormModal';
import { useNavigate } from 'react-router-dom';
import SalesOrderViewModal from './SalesOrderViewModal';
import PDFDesigner from '../components/pdfDesigner';
import axios from 'axios';
import { useScreenPermissions } from '../components/useScreenPermissions';
 
const API_URL = '/api';
 
const SalesOrderItems = () => {
  /* ---------- State ---------- */
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedSO, setSelectedSO] = useState(null);
  const navigate = useNavigate();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [isPDFDesignerOpen, setIsPDFDesignerOpen] = useState(false);
  const [selectedSOForPDF, setSelectedSOForPDF] = useState(null);
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  const [stats, setStats] = useState({
    totalSOs: 0,
    activeSOs: 0,
    inactiveSOs: 0,
    createdThisMonth: 0,
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  /* ---------- Sidebar theme integration (added) ---------- */
  // This injects CSS variables and scoped overrides so the Sidebar matches this page's theme.
  const pageTheme = useMemo(() => ({
    bg: '#f3f4f6',           // page background (gray-100)
    sidebarBg: '#ffffff',    // sidebar background
    accent: '#1d4ed8',       // blue-700 (use this for active item and create button if needed)
    accentHover: '#2563eb',  // blue-600 for hover states
    border: '#d1d5db',       // page/card borders
    text: '#0f1724',         // primary text
    muted: '#6b7280',        // muted text
    iconColor: '#1f2937',    // icon color
    hoverBg: '#eff6ff',      // hover background
    logoutBg: '#1d4ed8',     // logout/profile background (same as accent)
  }), []);

  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': pageTheme.border,
      '--sidebar-bg': pageTheme.sidebarBg,
      '--sidebar-text': pageTheme.text,
      '--accent-color': pageTheme.accent,
      '--accent-hover': pageTheme.accentHover,
      '--page-border-color': pageTheme.border,
      '--page-bg': pageTheme.bg,
      '--text-primary': pageTheme.text,
      '--text-muted': pageTheme.muted,
      '--icon-color': pageTheme.iconColor,
      '--hover-bg': pageTheme.hoverBg,
      '--logout-bg': pageTheme.logoutBg,
    };

    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    const styleId = 'salesorder-sidebar-overrides';
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      /* Sidebar base: white background, dark text for non-active items */
      .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--sidebar-text) !important; }
      .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item, .sidebar .nav-link, .app-sidebar .nav-link { color: var(--sidebar-text) !important; background-color: #ffffff !important; }

      /* Active item: use accent background with white text & icons */
      .sidebar a.active, .sidebar .nav-link.active, .sidebar .menu-item.active, .sidebar .nav-item.active,
      .app-sidebar a.active, .app-sidebar .nav-link.active {
        background-color: var(--accent-color) !important;
        color: #ffffff !important;
        font-weight: 700;
      }

      /* Force all nested text inside the active item to be white */
      .sidebar a.active *, .sidebar .menu-item.active *, .sidebar .nav-link.active *, .app-sidebar a.active *, .app-sidebar .menu-item.active *, .app-sidebar .nav-link.active * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Specific common label selectors some sidebars use */
      .sidebar a.active .menu-text,
      .sidebar a.active .menu-item-text,
      .sidebar a.active .item-label,
      .sidebar .menu-item.active .menu-text,
      .sidebar .menu-item.active .menu-item-text,
      .sidebar .menu-item.active .item-label {
        color: #ffffff !important;
        opacity: 1 !important;
      }

      /* Ensure active icons also use white so they are visible on accent background */
      .sidebar a.active svg, .sidebar .nav-link.active svg, .app-sidebar a.active svg {
        color: #ffffff !important;
        fill: #ffffff !important;
      }

      /* If sidebar implementation used an inset bar, keep it subtle or remove */
      .sidebar a.active::before, .app-sidebar a.active::before {
        background: transparent !important;
      }

      /* Catch-all: force white text for elements that use blue accent classes/inline styles */
      .sidebar .bg-blue-600, .sidebar .bg-blue-700,
      .app-sidebar .bg-blue-600, .app-sidebar .bg-blue-700,
      .sidebar [class*="bg-blue-600"], .sidebar [class*="bg-blue-700"],
      .app-sidebar [class*="bg-blue-600"], .app-sidebar [class*="bg-blue-700"],
      .sidebar [style*="#1d4ed8"], .sidebar [style*="#2563eb"],
      .app-sidebar [style*="#1d4ed8"], .app-sidebar [style*="#2563eb"],
      .sidebar [style*="background-color: var(--accent-color)"], .app-sidebar [style*="background-color: var(--accent-color)"] {
        color: #ffffff !important;
      }

      .sidebar .bg-blue-600 *, .sidebar .bg-blue-700 *,
      .app-sidebar .bg-blue-600 *, .app-sidebar .bg-blue-700 *,
      .sidebar [class*="bg-blue-600"] *, .sidebar [class*="bg-blue-700"] *,
      .app-sidebar [class*="bg-blue-600"] *, .app-sidebar [class*="bg-blue-700"] *,
      .sidebar [style*="#1d4ed8"] *, .sidebar [style*="#2563eb"] *,
      .app-sidebar [style*="#1d4ed8"] *, .app-sidebar [style*="#2563eb"] *,
      .sidebar [style*="background-color: var(--accent-color)"] *, .app-sidebar [style*="background-color: var(--accent-color)"] * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Profile avatar / icon in sidebar: use logout bg (accent) with white icon/text */
      .sidebar .profile .avatar, .sidebar .profile .icon, .sidebar .profile svg,
      .app-sidebar .profile .avatar, .app-sidebar .profile .icon, .app-sidebar .profile svg {
        background-color: var(--logout-bg) !important;
        color: #ffffff !important;
        fill: #ffffff !important;
      }

      /* Logout button style: accent bg with white text */
      .sidebar .logout-button, .app-sidebar .logout-button,
      .sidebar .profile-action, .sidebar .logout, .app-sidebar .logout,
      .sidebar .logout-btn, .app-sidebar .logout-btn {
        background-color: var(--logout-bg) !important;
        color: #ffffff !important;
        border-radius: 4px !important;
        padding: 6px 10px !important;
      }

      /* Ensure non-active sidebar items remain white with dark text */
      .sidebar .menu-item, .app-sidebar .menu-item, .sidebar .nav-link, .app-sidebar .nav-link {
        background-color: #ffffff !important;
        color: var(--sidebar-text) !important;
      }

      /* hover */
      .sidebar a:hover, .app-sidebar a:hover { background-color: rgba(0,0,0,0.03) !important; }
    `;

    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [pageTheme]);
  /* ---------- End sidebar theme integration ---------- */
 
  /* ---------- Data fetching ---------- */
  useEffect(() => {
    fetchSalesOrders();
  }, []);
 
  const fetchSalesOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/sales-order`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Failed to fetch sales orders');
      const data = await response.json();
      setSalesOrders(data);
      calculateStats(data);
    } catch (err) {
      console.error(err);
      alert('Error loading sales orders');
    } finally {
      setLoading(false);
    }
  };
   const handleLogout = () => {
    navigate('/login');
  };
  const calculateStats = (sos) => {
    const now = new Date();
    const curMonth = now.getMonth();
    const curYear = now.getFullYear();
    const totalSOs = sos.length;
    const activeSOs = sos.filter((so) => so.IsActive === 1 || so.IsActive === true).length;
    const inactiveSOs = totalSOs - activeSOs;
    const createdThisMonth = sos.filter((so) => {
      const d = new Date(so.SalesOrderDate);
      return d.getMonth() === curMonth && d.getFullYear() === curYear;
    }).length;
    setStats({ totalSOs, activeSOs, inactiveSOs, createdThisMonth });
  };
 
  /* ---------- CSV Export ---------- */
  const handleDownloadCSV = () => {
    const headers = [
      'SO Number',
      'SO Date',
      'Customer Name',
      'Total Items',
      'Total Quantity',
      'Total Amount',
      'Status',
    ].join(',');
    const rows = salesOrders.map((so) => {
      const items = so.Items || [];
      const totalItems = items.length;
      const totalQty = items.reduce((s, i) => s + (i.Quantity || 0), 0);
      const totalAmt = items.reduce((s, i) => s + (i.Total || 0), 0);
      const status = so.IsActive === 1 || so.IsActive === true ? 'Active' : 'Inactive';
      return [
        so.SalesOrderNumber,
        so.SalesOrderDate,
        so.customer_name || '',
        totalItems,
        totalQty,
        totalAmt,
        status,
      ].join(',');
    });
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sales_orders.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
 
  /* ---------- PDF Download ---------- */
  const downloadSOPDF = async (soNumber) => {
    try {
      const res = await axios.get(`${API_URL}/designs/Sales%20Order/pdf/so/${soNumber}`, {
        responseType: 'arraybuffer',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SO_${soNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Failed to download PDF');
    }
  };
 
  /* ---------- CRUD Handlers ---------- */
  const handleCreate = () => {
    setSelectedSO(null);
    setShowModal(true);
  };
  const handleEdit = (so) => {
    setSelectedSO(so);
    setShowModal(true);
  };
  const handleView = (so) => {
    setSelectedSO(so);
    setViewModalOpen(true);
  };
  const handleDelete = async (soNumber) => {
    if (!window.confirm(`Delete SO ${soNumber}?`)) return;
    try {
      const res = await fetch(`${API_URL}/sales-order/${soNumber}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      fetchSalesOrders();
      alert('Deleted');
    } catch (err) {
      alert('Cannot delete – items may be dispatched');
    }
  };
  const toggleSOStatus = async (soNumber, curActive) => {
    try {
      const newActive = curActive ? 0 : 1;
      const payload = { ...selectedSO, IsActive: newActive };
      const res = await fetch(`${API_URL}/sales-order/${soNumber}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Update failed');
      fetchSalesOrders();
    } catch (err) {
      alert('Status update failed');
    }
  };
  const handleModalSubmit = () => fetchSalesOrders();
 
  /* ---------- Sorting ---------- */
  const handleSort = (key) => {
    const dir = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction: dir });
  };
 
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.key) {
        case 'SalesOrderNumber':
        case 'customer_name':
          aVal = a[sortConfig.key] || '';
          bVal = b[sortConfig.key] || '';
          break;
        case 'SalesOrderDate':
          aVal = new Date(a[sortConfig.key]);
          bVal = new Date(b[sortConfig.key]);
          break;
        case 'totalItems':
          aVal = a.Items?.length || 0;
          bVal = b.Items?.length || 0;
          break;
        case 'totalQuantity':
          aVal = a.Items?.reduce((s, i) => s + (i.Quantity || 0), 0) || 0;
          bVal = b.Items?.reduce((s, i) => s + (i.Quantity || 0), 0) || 0;
          break;
        case 'totalRemainingQty':
          aVal = a.Items?.reduce((s, i) => s + (i.RemainingQty || 0), 0) || 0;
          bVal = b.Items?.reduce((s, i) => s + (i.RemainingQty || 0), 0) || 0;
          break;
        case 'totalAmount':
          aVal = a.Items?.reduce((s, i) => s + (i.Total || 0), 0) || 0;
          bVal = b.Items?.reduce((s, i) => s + (i.Total || 0), 0) || 0;
          break;
        case 'IsActive':
          aVal = a.IsActive ? 1 : 0;
          bVal = b.IsActive ? 1 : 0;
          break;
        default:
          aVal = a[sortConfig.key];
          bVal = b[sortConfig.key];
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
 
  const renderSortArrows = (col) => {
    const active = sortConfig.key === col;
    return (
      <span className="ml-2 flex flex-col">
        <FaSortUp className={`h-3 w-3 ${active && sortConfig.direction === 'asc' ? 'text-blue-600' : 'text-gray-500'}`} />
        <FaSortDown className={`h-3 w-3 -mt-1.5 ${active && sortConfig.direction === 'desc' ? 'text-blue-600' : 'text-gray-500'}`} />
      </span>
    );
  };
 
  /* ---------- Filtering & Pagination ---------- */
  const filteredSOs = salesOrders.filter((so) => {
    const matchesSearch =
      so.SalesOrderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (so.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const active = so.IsActive === 1 || so.IsActive === true;
    const matchesStatus =
      status === 'All' || (status === 'Active' && active) || (status === 'Inactive' && !active);
    return matchesSearch && matchesStatus;
  });
 
  const sortedSOs = getSortedData(filteredSOs);
  const totalPages = Math.ceil(sortedSOs.length / itemsPerPage);
  const currentItems = sortedSOs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
 
  const renderPagination = () => {
    const buttons = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
 
    buttons.push(
      <button
        key="prev"
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        className={`p-2 transition-all duration-200 ${
          currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'
        }`}
      >
        <FaChevronLeft className="h-5 w-5" />
      </button>
    );
 
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-4 py-1 font-medium transition-all duration-200 ${
            i === currentPage
              ? 'bg-blue-600 text-white'
              : 'text-blue-600 hover:bg-blue-50'
          }`}
        >
          {i}
        </button>
      );
    }
 
    buttons.push(
      <button
        key="next"
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
        className={`p-2 transition-all duration-200 ${
          currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'
        }`}
      >
        <FaChevronRight className="h-5 w-5" />
      </button>
    );
 
    return buttons;
  };
 
  /* ---------- Permissions ---------- */
  const canCreate = hasFullAccess || hasPermission('SALES_ORDER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('SALES_ORDER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('SALES_ORDER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('SALES_ORDER', 'CanDelete');
 
  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg font-medium text-gray-700">Loading permissions...</p>
        </div>
      </div>
    );
  }
 
  return (
    <div className="min-h-screen flex bg-gray-100">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff', profileBg: pageTheme.logoutBg }} 
      onLogout={handleLogout}/>
 
      <main
        className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300
           ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}
      >
        {/* ---------- Header ---------- */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-300 pb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">Sales Order Management</h1>
            <p className="mt-1 text-base text-gray-600">Streamlined tracking and management of sales orders</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadCSV}
              className="flex items-center gap-2 px-5 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 font-medium transition-all duration-200 shadow-sm"
            >
              <FaDownload className="h-4 w-4" />
              Export CSV
            </button>
            {canCreate && (
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all duration-200 shadow-sm"
              >
                <FaPlus className="h-4 w-4" />
                Create Order
              </button>
            )}
            <button
              onClick={() => {
                if (currentItems[0]) {
                  setSelectedSOForPDF(currentItems[0]);
                  setIsPDFDesignerOpen(true);
                }
              }}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all duration-200 shadow-sm"
            >
              <FaEdit className="h-4 w-4" />
              Design PDF
            </button>
          </div>
        </header>
 
        {/* ---------- Stats Cards ---------- */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Orders', value: stats.totalSOs, icon: 'document', color: 'blue' },
            { label: 'Active Orders', value: stats.activeSOs, icon: 'check', color: 'green' },
            { label: 'Inactive Orders', value: stats.inactiveSOs, icon: 'clock', color: 'amber' },
            { label: 'Orders This Month', value: stats.createdThisMonth, icon: 'calendar', color: 'purple' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="p-5 bg-white border border-gray-300 shadow-md hover:shadow-lg transition-shadow duration-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div
                  className={`w-10 h-10 flex items-center justify-center bg-${stat.color}-100 text-${stat.color}-600`}
                >
                  {stat.icon === 'document' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V4a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {stat.icon === 'check' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {stat.icon === 'clock' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {stat.icon === 'calendar' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))}
        </section>
 
        {/* ---------- Search & Filter ---------- */}
        <section className="p-5 bg-white border border-gray-300 shadow-md mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Search Orders</label>
              <input
                type="text"
                placeholder="Search by SO number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <button
              onClick={fetchSalesOrders}
              className="px-5 py-2 bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all duration-200 shadow-sm"
            >
              Apply
            </button>
          </div>
        </section>
 
        {/* ---------- Table ---------- */}
        <section className="bg-white border border-gray-300 shadow-md">
          <div className="flex justify-between items-center p-4 border-b border-gray-300">
            <h2 className="text-xl font-semibold text-gray-900">Order List</h2>
            <div className="flex gap-2">{renderPagination()}</div>
          </div>
 
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : currentItems.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No sales orders found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-200 text-gray-600 text-xs text-left font-semibold uppercase">
                  <tr>
                    {[
                      { key: 'SalesOrderNumber', label: 'SO Number' },
                      { key: 'SalesOrderDate', label: 'SO Date' },
                      { key: 'customer_name', label: 'Customer' },
                      { key: 'totalItems', label: 'Items' },
                      { key: 'totalQuantity', label: 'Qty' },
                      { key: 'totalRemainingQty', label: 'Remaining Qty' },
                      { key: 'totalAmount', label: 'Amount' },
                      { key: 'IsActive', label: 'Status' },
                      { key: 'actions', label: 'Actions' },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200"
                        onClick={() => col.key !== 'actions' && handleSort(col.key)}
                      >
                        <div className="flex items-center">
                          {col.label}
                          {col.key !== 'actions' && renderSortArrows(col.key)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300">
                  {currentItems.map((so) => {
                    const items = so.Items || [];
                    const totalItems = items.length;
                    const totalQty = items.reduce((s, i) => s + (i.Quantity || 0), 0);
                    const totalAmt = items.reduce((s, i) => s + (i.Total || 0), 0);
                    const remQty = items.reduce((s, i) => s + (i.RemainingQty || 0), 0);
                    const active = so.IsActive === 1 || so.IsActive === true;
 
                    return (
                      <tr key={so.SalesOrderNumber} className="hover:bg-gray-50 text-left transition-colors duration-200">
                        <td className="px-6 py-3 font-medium text-gray-900">{so.SalesOrderNumber}</td>
                        <td className="px-6 py-3 text-gray-600">{new Date(so.SalesOrderDate).toLocaleDateString()}</td>
                        <td className="px-6 py-3 text-gray-600">{so.customer_name || '-'}</td>
                        <td className="px-6 py-3 text-gray-600">{totalItems}</td>
                        <td className="px-6 py-3 text-gray-600">{totalQty}</td>
                        <td className="px-6 py-3 text-gray-600">{remQty}</td>
                        <td className="px-6 py-3 text-gray-600">{totalAmt.toFixed(2)}</td>
                        <td className="px-6 py-3">
                          <span
                            onClick={() => toggleSOStatus(so.SalesOrderNumber, active)}
                            className={`inline-block px-3 py-1 text-xs font-medium cursor-pointer rounded-full transition-colors duration-200 ${
                              active
                                ? 'bg-teal-50 text-teal-600 hover:bg-teal-300'
                                : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                            }`}
                          >
                            {active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex gap-3 justify-center">
                            {canView && (
                              <button
                                onClick={() => handleView(so)}
                                title="View Details"
                                className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                              >
                                <FaEye className="h-4 w-4" />
                              </button>
                            )}
                            {canEdit && (
                              <button
                                onClick={() => handleEdit(so)}
                                title="Edit Order"
                                className="text-green-600 hover:text-green-800 transition-colors duration-200"
                              >
                                <FaEdit className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(so.SalesOrderNumber)}
                                title="Delete Order"
                                className="text-red-600 hover:text-red-800 transition-colors duration-200"
                              >
                                <FaTrash className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => downloadSOPDF(so.SalesOrderNumber)}
                              title="Download PDF"
                              className="text-purple-600 hover:text-purple-800 transition-colors duration-200"
                              >
                              <FaDownload className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
 
        {/* ---------- Modals ---------- */}
        <SalesOrderFormModal
          open={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleModalSubmit}
          initialData={selectedSO}
          isEdit={!!selectedSO}
        />
        <SalesOrderViewModal open={viewModalOpen} onClose={() => setViewModalOpen(false)} soId={selectedSO} />
 
        {/* ---------- PDF Designer Modal ---------- */}
        {isPDFDesignerOpen && selectedSOForPDF && (
          <PDFDesigner
            isOpen={isPDFDesignerOpen}
            onClose={() => setIsPDFDesignerOpen(false)}
            entity="Sales Order"
            sampleData={{
              SalesOrderNumber: selectedSOForPDF.SalesOrderNumber,
              SalesOrderDate: new Date(selectedSOForPDF.SalesOrderDate).toLocaleDateString(),
              CustomerCode: selectedSOForPDF.CustomerCode || '-',
              CustomerName: selectedSOForPDF.CustomerName || '-',
              CustomerContact: selectedSOForPDF.CustomerContactName || '-',
              Status: selectedSOForPDF.IsActive ? 'Active' : 'Inactive',
              Currency: selectedSOForPDF.CurrencyCode || 'INR',
              DeliveryDate: selectedSOForPDF.DeliveryDate || '-',
              PaymentTerms: selectedSOForPDF.PaymentTerm || '-',
              Branch: `${selectedSOForPDF.BranchCode?.[0] || '-'} ${selectedSOForPDF.BranchName || ''}`,
              Warehouse: selectedSOForPDF.LocationCode || '-',
              Remarks: selectedSOForPDF.Remarks || '-',
              CustomerOrderNumber: selectedSOForPDF.CustomerOrderNumber?.[0] || '-',
              CustomerOrderDate: selectedSOForPDF.CustomerOrderDate?.[0] || '-',
              CustomerDueDate: selectedSOForPDF.CustomerDueDate?.[0] || '-',
              CustomerContactEmail: selectedSOForPDF.CustomerContactEmail || '-',
              CustomerContactPhone: selectedSOForPDF.CustomerContactPhone || '-',
              BillingAddressCode: selectedSOForPDF.BillingAddressCode || '-',
              BillingContactPerson: selectedSOForPDF.BillingContactPersonName || '-',
              BillingContactNumber: selectedSOForPDF.BillingContactNumber || '-',
              BillingAddressLine1: selectedSOForPDF.BillingAddressLine1 || '-',
              BillingCity: selectedSOForPDF.BillingCity || '-',
              BillingState: selectedSOForPDF.BillingState || '-',
              BillingZipCode: selectedSOForPDF.BillingZipCode || '-',
              BillingCountry: selectedSOForPDF.BillingCountry || '-',
              BillingGSTNumber: selectedSOForPDF.BillingGSTNumber || '-',
              BillingPANNumber: selectedSOForPDF.BillingPANNumber || '-',
              ShippingAddressCode: selectedSOForPDF.ShippingAddressCode || '-',
              ShippingContactPerson: selectedSOForPDF.ShippingContactPersonName || '-',
              ShippingContactNumber: selectedSOForPDF.ShippingContactNumber || '-',
              ShippingAddressLine1: selectedSOForPDF.ShippingAddressLine1 || '-',
              ShippingCity: selectedSOForPDF.ShippingCity || '-',
              ShippingState: selectedSOForPDF.ShippingState || '-',
              ShippingZipCode: selectedSOForPDF.ShippingZipCode || '-',
              ShippingCountry: selectedSOForPDF.ShippingCountry || '-',
              ShippingGSTNumber: selectedSOForPDF.ShippingGSTNumber || '-',
              ShippingPANNumber: selectedSOForPDF.ShippingPANNumber || '-',
              Items: (selectedSOForPDF.Items || []).map((i) => ({
                ProductCode: i.ProductCode,
                ProductName: i.ProductName,
                Description: i.Description || '-',
                UOM: i.UOM,
                Quantity: i.Quantity,
                RemainingQty: i.RemainingQty,
                UnitPrice: i.UnitPrice,
                Discount: i.Discount,
                Tax: i.TaxName || i.TaxCode || '-',
                TaxRate: i.TaxPercentage,
                TaxAmount: i.TaxAmount,
                Total: i.Total,
              })),
              Summary: {
                TotalItems: selectedSOForPDF.Items?.length || 0,
                TotalQuantity: selectedSOForPDF.Items?.reduce((s, i) => s + (i.Quantity || 0), 0) || 0,
                TotalRemainingQty: selectedSOForPDF.Items?.reduce((s, i) => s + (i.RemainingQty || 0), 0) || 0,
                GrandTotal: selectedSOForPDF.Items?.reduce((s, i) => s + (i.Total || 0), 0) || 0,
              },
            }}
            availableFields={{
              Header: [
                'SalesOrderNumber',
                'SalesOrderDate',
                'CustomerCode',
                'CustomerName',
                'CustomerContact',
                'Status',
                'Currency',
                'DeliveryDate',
                'PaymentTerms',
                'Branch',
                'Warehouse',
                'Remarks',
                'CustomerOrderNumber',
                'CustomerOrderDate',
                'CustomerDueDate',
                'CustomerContactEmail',
                'CustomerContactPhone',
                'BillingAddressCode',
                'BillingContactPerson',
                'BillingContactNumber',
                'BillingAddressLine1',
                'BillingCity',
                'BillingState',
                'BillingZipCode',
                'BillingCountry',
                'BillingGSTNumber',
                'BillingPANNumber',
                'ShippingAddressCode',
                'ShippingContactPerson',
                'ShippingContactNumber',
                'ShippingAddressLine1',
                'ShippingCity',
                'ShippingState',
                'ShippingZipCode',
                'ShippingCountry',
                'ShippingGSTNumber',
                'ShippingPANNumber',
              ],
              Items: [
                'ProductCode',
                'ProductName',
                'Description',
                'UOM',
                'Quantity',
                'RemainingQty',
                'UnitPrice',
                'Discount',
                'Tax',
                'TaxRate',
                'TaxAmount',
                'Total',
              ],
              Summary: ['TotalItems', 'TotalQuantity', 'TotalRemainingQty', 'GrandTotal'],
            }}
          />
        )}
      </main>
    </div>
  );
};
 
export default SalesOrderItems;
